import type { Locale } from "./index";

// Chinese month and weekday names are built here rather than left to Intl, so the teacher's dates
// read exactly as the student app's do. English still goes through Intl.
const ZH_WEEKDAY_SHORT = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];
const ZH_WEEKDAY_NARROW = ["日", "一", "二", "三", "四", "五", "六"];

const isZh = (locale: Locale) => locale !== "en";

const english = (options: Intl.DateTimeFormatOptions, date: Date) =>
  new Intl.DateTimeFormat("en", options).format(date);

/** Month index is 0-based, as on JS `Date`. */
export function monthShort(locale: Locale, month: number): string {
  if (isZh(locale)) return `${month + 1}月`;
  return english({ month: "short" }, new Date(2001, month, 1));
}

/** Weekday index is 0 for Sunday, as on JS `Date`. */
export function weekdayNarrow(locale: Locale, weekday: number): string {
  if (isZh(locale)) return ZH_WEEKDAY_NARROW[weekday] ?? "";
  // 7 January 2001 was a Sunday.
  return english({ weekday: "narrow" }, new Date(2001, 0, 7 + weekday));
}

/** "Oct 2026" / "2026年10月" */
export function formatMonthYear(locale: Locale, date: Date): string {
  if (isZh(locale)) return `${date.getFullYear()}年${date.getMonth() + 1}月`;
  return english({ month: "long", year: "numeric" }, date);
}

/** "Sep 19, 2028" / "2028年9月19日" */
export function formatYearMonthDay(locale: Locale, date: Date): string {
  if (isZh(locale)) {
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  }
  return english({ year: "numeric", month: "short", day: "numeric" }, date);
}

/** "Saturday, October 10" / "10月10日 週六" */
export function formatWeekdayMonthDayLong(locale: Locale, date: Date): string {
  if (isZh(locale)) {
    return `${date.getMonth() + 1}月${date.getDate()}日 ${ZH_WEEKDAY_SHORT[date.getDay()] ?? ""}`;
  }
  return english({ weekday: "long", month: "long", day: "numeric" }, date);
}
